import React, { useState, useEffect } from 'react';
import { Plus, Search, Upload, Edit2, Trash2, X, AlertCircle, ChevronUp, ChevronDown, Package } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

const CATEGORIES = ['Electronics', 'Clothing', 'Groceries', 'Home', 'Beauty', 'Sports', 'Accessories', 'Monitors', 'Other'];
const STOCK_STATUSES = ['in_stock', 'low_stock', 'out_of_stock'];
const PRODUCT_STATUSES = ['active', 'inactive', 'promotional'];
const STATUS_COLORS = { active: '#10B981', inactive: '#64748B', promotional: '#F59E0B' };
const STOCK_COLORS = { in_stock: '#10B981', low_stock: '#F59E0B', out_of_stock: '#EF4444' };

function ConfirmDialog({ message, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}>
      <div className="w-full max-w-sm rounded-2xl p-6" style={{ background: '#0D1B2A', border: '1px solid rgba(239,68,68,0.2)' }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.1)' }}>
            <Trash2 size={18} style={{ color: '#EF4444' }} />
          </div>
          <h3 className="text-base font-semibold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Confirm Delete</h3>
        </div>
        <p className="text-sm mb-5" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>{message}</p>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 py-2.5 rounded-xl text-sm font-medium" style={{ background: 'rgba(255,255,255,0.05)', color: '#94A3B8', border: '1px solid rgba(255,255,255,0.08)', fontFamily: 'DM Sans, sans-serif' }}>Cancel</button>
          <button onClick={onConfirm} className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white" style={{ background: '#EF4444', fontFamily: 'DM Sans, sans-serif' }}>Delete</button>
        </div>
      </div>
    </div>
  );
}

function ProductForm({ product, onSave, onCancel, partnerId }) {
  const [form, setForm] = useState(product || {
    product_name: '', sku: '', brand: '', category: 'Electronics',
    price: '', image_url: '', description: '', stock_status: 'in_stock', product_status: 'active'
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, price: parseFloat(form?.price) || 0, partner_id: partnerId };
      let res;
      if (product?.id) {
        res = await supabase?.from('retail_products')?.update(payload)?.eq('id', product?.id)?.select()?.single();
      } else {
        res = await supabase?.from('retail_products')?.insert(payload)?.select()?.single();
      }
      if (res?.error) { setError(res?.error?.message); return; }
      onSave(res?.data);
    } catch (e) { setError('Failed to save product'); } finally { setSaving(false); }
  };

  const inp = (label, key, type = 'text', opts = {}) => (
    <div>
      <label className="block text-xs font-medium mb-1.5" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>{label}</label>
      {type === 'select' ? (
        <select value={form?.[key]} onChange={e => setForm(f => ({ ...f, [key]: e?.target?.value }))}
          className="w-full px-3 py-2.5 rounded-xl text-sm text-white outline-none"
          style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.15)', fontFamily: 'DM Sans, sans-serif' }}>
          {opts?.options?.map(o => <option key={o} value={o} style={{ background: '#0D1B2A' }}>{o?.replace(/_/g, ' ')}</option>)}
        </select>
      ) : type === 'textarea' ? (
        <textarea value={form?.[key]} onChange={e => setForm(f => ({ ...f, [key]: e?.target?.value }))}
          rows={3} placeholder={opts?.placeholder}
          className="w-full px-3 py-2.5 rounded-xl text-sm text-white placeholder-slate-600 outline-none resize-none"
          style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.15)', fontFamily: 'DM Sans, sans-serif' }} />
      ) : (
        <input type={type} value={form?.[key]} onChange={e => setForm(f => ({ ...f, [key]: e?.target?.value }))}
          placeholder={opts?.placeholder} required={opts?.required}
          className="w-full px-3 py-2.5 rounded-xl text-sm text-white placeholder-slate-600 outline-none"
          style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.15)', fontFamily: 'DM Sans, sans-serif' }} />
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}>
      <div className="w-full max-w-lg rounded-2xl p-6 max-h-[90vh] overflow-y-auto" style={{ background: '#0D1B2A', border: '1px solid rgba(0,229,255,0.15)' }}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-semibold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>{product?.id ? 'Edit Product' : 'Add New Product'}</h3>
          <button onClick={onCancel} style={{ color: '#64748B' }}><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {inp('Product Name *', 'product_name', 'text', { placeholder: 'Product name', required: true })}
            {inp('SKU', 'sku', 'text', { placeholder: 'SKU-001' })}
            {inp('Brand', 'brand', 'text', { placeholder: 'Brand name' })}
            {inp('Category', 'category', 'select', { options: CATEGORIES })}
            {inp('Price ($)', 'price', 'number', { placeholder: '0.00' })}
            {inp('Stock Status', 'stock_status', 'select', { options: STOCK_STATUSES })}
            {inp('Status', 'product_status', 'select', { options: PRODUCT_STATUSES })}
          </div>
          {inp('Image URL', 'image_url', 'text', { placeholder: 'https://...' })}
          {inp('Description', 'description', 'textarea', { placeholder: 'Product description...' })}
          {error && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
              <AlertCircle size={14} style={{ color: '#EF4444' }} />
              <p className="text-xs" style={{ color: '#EF4444', fontFamily: 'DM Sans, sans-serif' }}>{error}</p>
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onCancel} className="flex-1 py-2.5 rounded-xl text-sm font-medium" style={{ background: 'rgba(255,255,255,0.05)', color: '#94A3B8', border: '1px solid rgba(255,255,255,0.08)', fontFamily: 'DM Sans, sans-serif' }}>Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white" style={{ background: 'linear-gradient(135deg, #00E5FF, #7B2FBE)', fontFamily: 'DM Sans, sans-serif' }}>
              {saving ? 'Saving...' : 'Save Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ProductCatalog({ partnerId }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [showForm, setShowForm] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [csvMsg, setCsvMsg] = useState({ type: '', text: '' });

  const fetchProducts = async () => {
    if (!partnerId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase?.from('retail_products')?.select('*')?.eq('partner_id', partnerId)?.order('created_at', { ascending: false });
      if (!error) setProducts(data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchProducts(); }, [partnerId]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await supabase?.from('retail_products')?.delete()?.eq('id', deleteTarget);
    setProducts(p => p?.filter(x => x?.id !== deleteTarget));
    setDeleteTarget(null);
  };

  const handleStatusCycle = async (product) => {
    const cycle = { active: 'inactive', inactive: 'promotional', promotional: 'active' };
    const newStatus = cycle?.[product?.product_status] || 'active';
    await supabase?.from('retail_products')?.update({ product_status: newStatus })?.eq('id', product?.id);
    setProducts(p => p?.map(x => x?.id === product?.id ? { ...x, product_status: newStatus } : x));
  };

  const handleSave = (saved) => {
    setProducts(p => {
      const exists = p?.find(x => x?.id === saved?.id);
      return exists ? p?.map(x => x?.id === saved?.id ? saved : x) : [saved, ...p];
    });
    setShowForm(false);
    setEditProduct(null);
  };

  const handleCSV = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setCsvMsg({ type: '', text: '' });
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const lines = ev?.target?.result?.split('\n')?.filter(Boolean);
        const headers = lines?.[0]?.split(',')?.map(h => h?.trim()?.toLowerCase()?.replace(/["']/g, ''));
        const rows = lines?.slice(1)?.map(line => {
          const vals = line?.split(',');
          const obj = {};
          headers?.forEach((h, i) => { obj[h] = vals?.[i]?.trim()?.replace(/["']/g, '') || ''; });
          return obj;
        });
        const prods = rows?.map(r => ({
          partner_id: partnerId,
          product_name: r?.['product_name'] || r?.['name'] || '',
          sku: r?.['sku'] || '',
          brand: r?.['brand'] || '',
          category: r?.['category'] || 'Other',
          price: parseFloat(r?.['price']) || 0,
          stock_status: r?.['stock_status'] || 'in_stock',
          image_url: r?.['image_url'] || r?.['image'] || '',
          description: r?.['description'] || '',
          product_status: 'active',
        }))?.filter(p => p?.product_name);
        if (!prods?.length) { setCsvMsg({ type: 'error', text: 'No valid products found in CSV' }); return; }
        const { error } = await supabase?.from('retail_products')?.insert(prods);
        if (error) { setCsvMsg({ type: 'error', text: error?.message }); return; }
        setCsvMsg({ type: 'success', text: `${prods?.length} products imported successfully!` });
        fetchProducts();
      } catch (e) { setCsvMsg({ type: 'error', text: 'Invalid CSV format' }); }
    };
    reader?.readAsText(file);
    e.target.value = '';
  };

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return null;
    return sortDir === 'asc' ? <ChevronUp size={12} style={{ color: '#00E5FF' }} /> : <ChevronDown size={12} style={{ color: '#00E5FF' }} />;
  };

  const filtered = products
    ?.filter(p => {
      const matchSearch = p?.product_name?.toLowerCase()?.includes(search?.toLowerCase()) || p?.sku?.toLowerCase()?.includes(search?.toLowerCase());
      const matchCat = filterCat === 'All' || p?.category === filterCat;
      const matchStatus = filterStatus === 'All' || p?.product_status === filterStatus;
      return matchSearch && matchCat && matchStatus;
    })
    ?.sort((a, b) => {
      let av = a?.[sortBy], bv = b?.[sortBy];
      if (sortBy === 'price') { av = parseFloat(av) || 0; bv = parseFloat(bv) || 0; }
      if (sortDir === 'asc') return av > bv ? 1 : -1;
      return av < bv ? 1 : -1;
    });

  const categories = ['All', ...new Set(products?.map(p => p?.category)?.filter(Boolean))];

  return (
    <div className="space-y-5">
      {(showForm || editProduct) && (
        <ProductForm product={editProduct} onSave={handleSave} onCancel={() => { setShowForm(false); setEditProduct(null); }} partnerId={partnerId} />
      )}
      {deleteTarget && (
        <ConfirmDialog
          message="Are you sure you want to delete this product? This action cannot be undone."
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Product Catalog</h2>
          <p className="text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{products?.length} products listed</p>
        </div>
        <div className="flex gap-2">
          <label className="flex items-center gap-2 px-4 py-2 rounded-xl cursor-pointer text-sm font-medium transition-all" style={{ background: 'rgba(0,229,255,0.08)', border: '1px solid rgba(0,229,255,0.15)', color: '#00E5FF', fontFamily: 'DM Sans, sans-serif' }}>
            <Upload size={15} /> Import CSV
            <input type="file" accept=".csv" className="hidden" onChange={handleCSV} />
          </label>
          <button onClick={() => setShowForm(true)} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white" style={{ background: 'linear-gradient(135deg, #00E5FF, #7B2FBE)', fontFamily: 'DM Sans, sans-serif' }}>
            <Plus size={15} /> Add Product
          </button>
        </div>
      </div>

      {csvMsg?.text && (
        <div className="px-4 py-2.5 rounded-xl text-sm flex items-center gap-2" style={{ background: csvMsg?.type === 'success' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${csvMsg?.type === 'success' ? 'rgba(16,185,129,0.2)' : 'rgba(239,68,68,0.2)'}`, color: csvMsg?.type === 'success' ? '#10B981' : '#EF4444', fontFamily: 'DM Sans, sans-serif' }}>
          {csvMsg?.text}
          <button onClick={() => setCsvMsg({ type: '', text: '' })} className="ml-auto"><X size={14} /></button>
        </div>
      )}

      <div className="px-4 py-2.5 rounded-xl" style={{ background: 'rgba(0,229,255,0.04)', border: '1px solid rgba(0,229,255,0.08)' }}>
        <p className="text-xs" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>CSV headers: <span style={{ color: '#00E5FF' }}>name, sku, brand, category, price, stock_status, image_url, description</span></p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#64748B' }} />
          <input value={search} onChange={e => setSearch(e?.target?.value)} placeholder="Search by name or SKU..."
            className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm text-white placeholder-slate-600 outline-none"
            style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.12)', fontFamily: 'DM Sans, sans-serif' }} />
        </div>
        <select value={filterCat} onChange={e => setFilterCat(e?.target?.value)}
          className="px-3 py-2.5 rounded-xl text-sm text-white outline-none"
          style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.12)', fontFamily: 'DM Sans, sans-serif' }}>
          {categories?.map(c => <option key={c} value={c} style={{ background: '#0D1B2A' }}>{c}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e?.target?.value)}
          className="px-3 py-2.5 rounded-xl text-sm text-white outline-none"
          style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(0,229,255,0.12)', fontFamily: 'DM Sans, sans-serif' }}>
          <option value="All" style={{ background: '#0D1B2A' }}>All Status</option>
          {PRODUCT_STATUSES?.map(s => <option key={s} value={s} style={{ background: '#0D1B2A' }}>{s}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(0,229,255,0.08)' }}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ background: 'rgba(0,0,0,0.3)', borderBottom: '1px solid rgba(0,229,255,0.08)' }}>
                <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Product</th>
                <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>SKU</th>
                <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Category</th>
                <th className="px-4 py-3 text-left text-xs font-semibold cursor-pointer select-none" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }} onClick={() => toggleSort('price')}>
                  <span className="flex items-center gap-1">Price <SortIcon col="price" /></span>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Stock</th>
                <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)]?.map((_, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    {[...Array(7)]?.map((_, j) => (
                      <td key={j} className="px-4 py-3"><div className="h-4 rounded animate-pulse" style={{ background: 'rgba(255,255,255,0.05)', width: j === 0 ? '140px' : '80px' }} /></td>
                    ))}
                  </tr>
                ))
              ) : filtered?.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <Package size={28} className="mx-auto mb-2" style={{ color: '#1E3A5F' }} />
                    <p className="text-sm" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>No products found</p>
                  </td>
                </tr>
              ) : (
                filtered?.map(p => (
                  <tr key={p?.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: 'rgba(255,255,255,0.01)' }}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {p?.image_url ? (
                          <img src={p?.image_url} alt={p?.product_name} className="w-8 h-8 rounded-lg object-cover" style={{ border: '1px solid rgba(0,229,255,0.1)' }} />
                        ) : (
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold text-white" style={{ background: 'linear-gradient(135deg, rgba(0,229,255,0.2), rgba(123,47,190,0.2))' }}>
                            {p?.product_name?.charAt(0)}
                          </div>
                        )}
                        <span className="text-sm text-white" style={{ fontFamily: 'DM Sans, sans-serif', maxWidth: '140px', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p?.product_name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: '#64748B', fontFamily: 'DM Sans, sans-serif' }}>{p?.sku || '—'}</td>
                    <td className="px-4 py-3 text-xs" style={{ color: '#94A3B8', fontFamily: 'DM Sans, sans-serif' }}>{p?.category}</td>
                    <td className="px-4 py-3 text-sm font-semibold" style={{ color: '#E2E8F0', fontFamily: 'DM Sans, sans-serif' }}>${parseFloat(p?.price || 0)?.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: `${STOCK_COLORS?.[p?.stock_status] || '#64748B'}15`, color: STOCK_COLORS?.[p?.stock_status] || '#64748B', fontFamily: 'DM Sans, sans-serif' }}>
                        {p?.stock_status?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleStatusCycle(p)}
                        className="text-xs px-2.5 py-1 rounded-full font-medium transition-all"
                        style={{ background: `${STATUS_COLORS?.[p?.product_status] || '#64748B'}15`, color: STATUS_COLORS?.[p?.product_status] || '#64748B', border: `1px solid ${STATUS_COLORS?.[p?.product_status] || '#64748B'}30`, fontFamily: 'DM Sans, sans-serif' }}
                      >
                        {p?.product_status}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button onClick={() => setEditProduct(p)} className="w-7 h-7 rounded-lg flex items-center justify-center transition-all" style={{ background: 'rgba(0,229,255,0.08)', color: '#00E5FF' }}>
                          <Edit2 size={13} />
                        </button>
                        <button onClick={() => setDeleteTarget(p?.id)} className="w-7 h-7 rounded-lg flex items-center justify-center transition-all" style={{ background: 'rgba(239,68,68,0.08)', color: '#EF4444' }}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
